import React, { useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { Toaster } from "./components/ui/sonner";
import GuidedTour from "./components/onboarding/GuidedTour";
import InteractiveTour from "./components/onboarding/InteractiveTour";
import OnboardingModal from "./components/onboarding/OnboardingModal";

// Auth Pages
import Login from "./pages/auth/Login";
import Register from "./pages/auth/Register";

// Dashboard Pages
import AgencyDashboard from "./pages/dashboard/AgencyDashboard";
import TeamDashboard from "./pages/dashboard/TeamDashboard";
import ClientDashboard from "./pages/dashboard/ClientDashboard";

// Project Pages
import ProjectsPage from "./pages/projects/ProjectsPage";
import ProjectDetailPage from "./pages/projects/ProjectDetailPage";
import NewProjectPage from "./pages/projects/NewProjectPage";

// Wallet Pages
import WalletPage from "./pages/wallet/WalletPage";
import WalletManagementPage from "./pages/wallet/WalletManagementPage";
import ClientsWalletsPage from "./pages/wallet/ClientsWalletsPage";

// Settings Pages
import ThemeSettingsPage from "./pages/settings/ThemeSettingsPage";
import CalendarSettingsPage from "./pages/settings/CalendarSettingsPage";
import PromoBannerSettingsPage from "./pages/settings/PromoBannerSettingsPage";
import NomenclatureSettingsPage from "./pages/settings/NomenclatureSettingsPage";
import AxonautIntegrationPage from "./pages/settings/AxonautIntegrationPage";
import AgencyInfoSettingsPage from "./pages/settings/AgencyInfoSettingsPage";
import StatusSettingsPage from "./pages/settings/StatusSettingsPage";

// Admin Pages
import ClientsPage from "./pages/clients/ClientsPage";
import ClientsPageV1 from "./pages/clients/ClientsPageV1";
import ClientDetailPage from "./pages/clients/ClientDetailPageV2";
import CreativesPage from "./pages/creatives/CreativesPage";
import CreativeDirectoryPage from "./pages/creatives/CreativeDirectoryPage";
import CreativeProfilePage from "./pages/creatives/CreativeProfilePage";
import ProjectOffersPage from "./pages/creatives/ProjectOffersPage";
import InvoicesPage from "./pages/invoices/InvoicesPage";
import ExpensesPage from "./pages/expenses/ExpensesPage";

// Profile Page
import ProfilePage from "./pages/profile/ProfilePage";
import MySchedulePage from "./pages/schedule/MySchedulePage";
import TicketsPage from "./pages/tickets/TicketsPage";
import TicketDetailPage from "./pages/tickets/TicketDetailPage";
import CampaignsPage from "./pages/campaigns/CampaignsPage";

// New Pages
import SupportsPage from "./pages/supports/SupportsPage";
import CompanyProfilePage from "./pages/company/CompanyProfilePage";
import AffiliatesPage from "./pages/affiliates/AffiliatesPage";
import AffiliatesPageV1 from "./pages/affiliates/AffiliatesPageV1";
import AffiliateContractsPage from "./pages/affiliates/AffiliateContractsPage";
import LinkTrackerPage from "./pages/tools/LinkTrackerPage";
import QRCodePage from "./pages/tools/QRCodePage";
import ContractsPage from "./pages/contracts/ContractsPage";
import ContractDetailPage from "./pages/contracts/ContractDetailPage";
import ContractSubscriptionPage from "./pages/contracts/ContractSubscriptionPage";
import MemberContractCreatePage from "./pages/contracts/MemberContractCreatePage";

// Prospects Pages
import ProspectsPageV1 from "./pages/prospects/ProspectsPageV1";
import ProspectsPage from "./pages/prospects/ProspectsPage";
import ProspectDetailPage from "./pages/prospects/ProspectDetailPage";
import PricingSettingsPage from "./pages/settings/PricingSettingsPage";
import StripeSettingsPage from "./pages/settings/StripeSettingsPage";
import KnowledgeBasePage from "./pages/knowledge/KnowledgeBasePage";
import TeamPlanningPage from "./pages/planning/TeamPlanningPage";
import NotificationsPage from "./pages/notifications/NotificationsPage";
import CreditsPage from "./pages/credits/CreditsPage";
import SocialDashboardPage from "./pages/social/SocialDashboardPage";
import AssistantPage from "./pages/assistant/AssistantPage";
import PaymentRequestsPage from "./pages/payments/PaymentRequestsPage";
import RatingsPage from "./pages/ratings/RatingsPage";
import BonusDashboardPage from "./pages/bonuses/BonusDashboardPage";
import MemberContractsPage from "./pages/member-contracts/MemberContractsPage";
import ClientNewsPage from "./pages/news/ClientNewsPage";
import MemberBonusesPage from "./pages/member-bonuses/MemberBonusesPage";
import MemberReportsPage from "./pages/reports/MemberReportsPage";
import ServiceInvoicesPage from "./pages/service-invoices/ServiceInvoicesPage";
import ScrollToTop from "./components/layout/ScrollToTop";
import MemberEditPage from "./pages/users/MemberEditPage";
import BugReportButton from "./components/widgets/BugReportButton";
import InstallAppButton from "./components/pwa/InstallAppButton";

// Onboarding Pages
import ClientOnboardingPage from "./pages/onboarding/ClientOnboardingPage";

// Admin & Develop Pages
import AdminPage from "./pages/admin/AdminPage";
import AdminToolsPage from "./pages/admin/AdminToolsPage";
import RoadmapPage from "./pages/admin/RoadmapPage";
import FeatureTrackingPage from "./pages/admin/FeatureTrackingPage";
import GiftCodesPage from "./pages/admin/GiftCodesPage";
import RecoveryManagementPage from "./pages/admin/RecoveryManagementPage";
import AnnouncementsAdminPage from "./pages/admin/AnnouncementsAdminPage";
import DevelopPage from "./pages/develop/DevelopPage";
import TeamOnboardingPage from "./pages/onboarding/TeamOnboardingPage";
import CreativeOnboardingPage from "./pages/onboarding/CreativeOnboardingPage";
import OnboardingManagementPage from "./pages/onboarding/OnboardingManagementPage";
import GuidedTourSettingsPage from "./pages/settings/GuidedTourSettingsPage";

// Invoices Page
import ClientInvoicesPage from "./pages/invoices/ClientInvoicesPage";

// Help Page
import HelpPage from "./pages/help/HelpPage";

// Auth Pages - Additional
import PendingApprovalPage from "./pages/auth/PendingApprovalPage";

// Client Pages
import ClientReceiptsPage from "./pages/client/ClientReceiptsPage";
import ClientAffiliatePage from "./pages/client/ClientAffiliatePage";
import ClientSchedulesPage from "./pages/client/ClientSchedulesPage";
import ClientContractsPage from "./pages/client/ClientContractsPage";
import ClientForfaitsPage from "./pages/client/ClientForfaitsPage";

// Receipts Pages
import PendingReceiptsPage from "./pages/receipts/PendingReceiptsPage";
import ReceiptsPageV1 from "./pages/receipts/ReceiptsPageV1";
import AgencyReceiptsPage from "./pages/receipts/AgencyReceiptsPage";
import FormBuilderPage from "./pages/settings/FormBuilderPage";
import TemplatesPage from "./pages/settings/TemplatesPage";
import CreateUserPage from "./pages/users/CreateUserPage";
import UsersPage from "./pages/users/UsersPage";
import TeamMembersPage from "./pages/team/TeamMembersPage";
import SmtpSettingsPage from "./pages/settings/SmtpSettingsPage";

// Affiliate Pages
import AffiliateDashboard from "./pages/affiliate/AffiliateDashboard";
import AffiliateLeadsPage from "./pages/affiliate/AffiliateLeadsPage";
import AffiliateCommissionsPage from "./pages/affiliate/AffiliateCommissionsPage";
import AffiliateContractPage from "./pages/affiliate/AffiliateContractPage";
import AffiliateOnboardingPage from "./pages/affiliate/AffiliateOnboardingPage";
import CustomerServiceDashboard from "./pages/customer-service/CustomerServiceDashboard";

// New Pages - Reports, Tasks, Offers, Wallet History
import ReportsPage from "./pages/reports/ReportsPage";
import TeamTasksPage from "./pages/tasks/TeamTasksPage";
import OffersPage from "./pages/offers/OffersPage";
import WalletHistoryPage from "./pages/wallet/WalletHistoryPage";
import QuotesManagementPage from "./pages/quotes/QuotesManagementPage";

// Calendar Page
import CalendarPage from "./pages/calendar/CalendarPage";
import ClientAppointmentsPage from "./pages/calendar/ClientAppointmentsPage";

// Project Requests Pages
import NewProjectRequestPage from "./pages/projects/NewProjectRequestPage";
import MyProjectRequestsPage from "./pages/projects/MyProjectRequestsPage";
import ProjectRequestsManagementPage from "./pages/projects/ProjectRequestsManagementPage";
import AgencyNewProjectPage from "./pages/projects/AgencyNewProjectPage";

// Shared Pages
import SharedPasswordsPage from "./pages/shared/SharedPasswordsPage";
import SharedSupportsPage from "./pages/shared/SharedSupportsPage";
import GoogleDriveManagementPage from "./pages/shared/GoogleDriveManagementPage";

// Agency Internal
import AgencyManifestPage from "./pages/agency/AgencyManifestPage";
import BriefTemplatesPage from "./pages/agency/BriefTemplatesPage";

// Intern Portal
import InternDashboardPage from "./pages/intern/InternDashboardPage";

// Admin Settings
import AdminSettingsPage from "./pages/admin/AdminSettingsPage";
import DemoModePage from "./pages/admin/DemoModePage";
import EmbedFormsPage from "./pages/admin/EmbedFormsPage";
import ChangelogPage from "./pages/admin/ChangelogPage";

// AI Summaries
import SummaryHistoryPage from "./pages/summaries/SummaryHistoryPage";

// Domains Management
import DomainsPage from "./pages/domains/DomainsPage";

// Email Settings
import EmailSettingsPage from "./pages/email-settings/EmailSettingsPage";
import MonthlyReportsPage from "./pages/reports/MonthlyReportsPage";
import ReportHistoryPage from "./pages/reports/ReportHistoryPage";
import TaskDetailPage from "./pages/tasks/TaskDetailPage";
import InternalChatPage from "./pages/chat/InternalChatPage";
import ProjectChatPage from "./pages/project-chat/ProjectChatPage";
import UnifiedChatPage from "./pages/chat/UnifiedChatPage";
import TaskChatPage from "./pages/chat/TaskChatPage";
import ContractChatPage from "./pages/chat/ContractChatPage";
import SavedLinksPage from "./pages/links/SavedLinksPage";
import TechnicalDocsPage from "./pages/docs/TechnicalDocsPage";

// Finance Pages
import HourlyRatesPage from "./pages/settings/HourlyRatesPage";
import ThirdPartyServicesPage from "./pages/finance/ThirdPartyServicesPage";
import PaymentSchedulesPage from "./pages/finance/PaymentSchedulesPage";

// Contract Templates
import ContractTemplatesPage from "./pages/contracts/ContractTemplatesPage";
import SmartContractsPage from "./pages/contracts/SmartContractsPage";
import ContractGeneratorPage from "./pages/contracts/ContractGeneratorPage";

// Satisfaction Surveys
import SatisfactionSurveysPage from "./pages/satisfaction/SatisfactionSurveysPage";
import SatisfactionPublicPage from "./pages/satisfaction/SatisfactionPublicPage";

// Public Pages
import PublicQuotePage from "./pages/public/PublicQuotePage";

// Contract Required Component for Team Members
import ContractRequired from "./components/auth/ContractRequired";

// Timer Context
import { TimerProvider } from "./contexts/TimerContext";

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles, skipContractCheck = false }) => {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8fafc]">
        <div className="spinner" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    if (user?.role === 'agency') {
      return <Navigate to="/dashboard/agency" replace />;
    } else if (user?.role === 'project_manager') {
      return <Navigate to="/dashboard/project-manager" replace />;
    } else if (user?.role === 'team_member') {
      if (user?.team_member_type === 'affiliate') {
        return <Navigate to="/affiliate/dashboard" replace />;
      }
      return <Navigate to="/dashboard/team" replace />;
    } else if (user?.role === 'affiliate') {
      return <Navigate to="/affiliate/dashboard" replace />;
    } else if (user?.role === 'customer_service') {
      return <Navigate to="/dashboard/customer-service" replace />;
    } else {
      return <Navigate to="/dashboard/client" replace />;
    }
  }

  // For team members, check contract status (unless skipContractCheck is true)
  if (user?.role === 'team_member' && !skipContractCheck) {
    return <ContractRequired>{children}</ContractRequired>;
  }

  return children;
};

// Public Route (redirect to dashboard if authenticated)
const PublicRoute = ({ children }) => {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8fafc]">
        <div className="spinner" />
      </div>
    );
  }

  if (isAuthenticated) {
    if (user?.role === 'agency') {
      return <Navigate to="/dashboard/agency" replace />;
    } else if (user?.role === 'project_manager') {
      return <Navigate to="/dashboard/project-manager" replace />;
    } else if (user?.role === 'team_member') {
      if (user?.team_member_type === 'affiliate') {
        return <Navigate to="/affiliate/dashboard" replace />;
      }
      return <Navigate to="/dashboard/team" replace />;
    } else if (user?.role === 'affiliate') {
      return <Navigate to="/affiliate/dashboard" replace />;
    } else if (user?.role === 'customer_service') {
      return <Navigate to="/dashboard/customer-service" replace />;
    } else {
      return <Navigate to="/dashboard/client" replace />;
    }
  }

  return children;
};

// Home redirect based on role
const HomeRedirect = () => {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8fafc]">
        <div className="spinner" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.role === 'agency') {
    return <Navigate to="/dashboard/agency" replace />;
  } else if (user?.role === 'project_manager') {
    return <Navigate to="/dashboard/project-manager" replace />;
  } else if (user?.role === 'team_member') {
    if (user?.team_member_type === 'affiliate') {
      return <Navigate to="/affiliate/dashboard" replace />;
    }
    return <Navigate to="/dashboard/team" replace />;
  } else if (user?.role === 'affiliate') {
    return <Navigate to="/affiliate/dashboard" replace />;
  } else if (user?.role === 'customer_service') {
    return <Navigate to="/dashboard/customer-service" replace />;
  } else {
    return <Navigate to="/dashboard/client" replace />;
  }
};

function AppRoutes() {
  return (
    <>
      <ScrollToTop />
      <GuidedTour />
      <InteractiveTour />
      <Routes>
        {/* Public Routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />
        <Route
          path="/register"
          element={
            <PublicRoute>
              <Register />
            </PublicRoute>
          }
        />

        {/* Home redirect */}
        <Route path="/" element={<HomeRedirect />} />

        {/* Agency Dashboard */}
        <Route
          path="/dashboard/agency"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager']}>
              <AgencyDashboard />
            </ProtectedRoute>
          }
        />

        {/* Project Manager Dashboard - same view as agency but with limited settings */}
        <Route
          path="/dashboard/project-manager"
          element={
            <ProtectedRoute allowedRoles={['project_manager']}>
              <AgencyDashboard />
            </ProtectedRoute>
          }
        />

        {/* Team Member Dashboard */}
        <Route
          path="/dashboard/team"
          element={
            <ProtectedRoute allowedRoles={['team_member', 'project_manager']}>
              <TeamDashboard />
            </ProtectedRoute>
          }
        />

        {/* Client Dashboard */}
        <Route
          path="/dashboard/client"
          element={
            <ProtectedRoute allowedRoles={['client']}>
              <ClientDashboard />
            </ProtectedRoute>
          }
        />

        {/* Customer Service Dashboard */}
        <Route
          path="/dashboard/customer-service"
          element={
            <ProtectedRoute allowedRoles={['customer_service', 'agency']}>
              <CustomerServiceDashboard />
            </ProtectedRoute>
          }
        />

        {/* Affiliate Dashboard */}
        <Route
          path="/dashboard/affiliate"
          element={
            <ProtectedRoute allowedRoles={['affiliate', 'team_member']}>
              <AffiliateDashboard />
            </ProtectedRoute>
          }
        />

        {/* Affiliate Pages */}
        <Route
          path="/affiliate/leads"
          element={
            <ProtectedRoute allowedRoles={['affiliate', 'team_member']}>
              <AffiliateLeadsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/affiliate/opportunities"
          element={
            <ProtectedRoute allowedRoles={['affiliate', 'team_member']}>
              <AffiliateLeadsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/affiliate/commissions"
          element={
            <ProtectedRoute allowedRoles={['affiliate', 'team_member', 'project_manager']}>
              <AffiliateCommissionsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/affiliate/contract"
          element={
            <ProtectedRoute allowedRoles={['affiliate', 'team_member']}>
              <AffiliateContractPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/affiliate/dashboard"
          element={
            <ProtectedRoute allowedRoles={['affiliate', 'team_member']}>
              <AffiliateDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/affiliate/referrals"
          element={
            <ProtectedRoute allowedRoles={['affiliate', 'team_member']}>
              <AffiliateLeadsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/affiliate/onboarding"
          element={
            <ProtectedRoute allowedRoles={['affiliate', 'team_member']}>
              <AffiliateOnboardingPage />
            </ProtectedRoute>
          }
        />

        {/* Pending Approval Page */}
        <Route path="/pending-approval" element={<PendingApprovalPage />} />

        {/* Public Satisfaction Survey Page */}
        <Route path="/satisfaction/:surveyId" element={<SatisfactionPublicPage />} />

        {/* Public Quote Request Page - Demande de devis publique */}
        <Route path="/devis" element={<PublicQuotePage />} />

        {/* Calendar */}
        <Route
          path="/calendar"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager', 'customer_service', 'team_member']}>
              <CalendarPage />
            </ProtectedRoute>
          }
        />
        
        {/* Client Appointments - Simplified view for clients */}
        <Route
          path="/my-appointments"
          element={
            <ProtectedRoute allowedRoles={['client']}>
              <ClientAppointmentsPage />
            </ProtectedRoute>
          }
        />

        {/* Projects */}
        <Route
          path="/projects"
          element={
            <ProtectedRoute allowedRoles={['agency', 'customer_service', 'team_member', 'project_manager']}>
              <ProjectsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/projects/:projectId"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager', 'customer_service', 'team_member', 'client']}>
              <ProjectDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/my-projects"
          element={
            <ProtectedRoute allowedRoles={['team_member', 'client']}>
              <ProjectsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/my-projects/new"
          element={
            <ProtectedRoute allowedRoles={['client']}>
              <NewProjectPage />
            </ProtectedRoute>
          }
        />

        {/* Project Requests - Client */}
        <Route
          path="/new-project-request"
          element={
            <ProtectedRoute allowedRoles={['client']}>
              <NewProjectRequestPage />
            </ProtectedRoute>
          }
        />
        {/* Redirect old requests pages to projects */}
        <Route
          path="/my-requests"
          element={<Navigate to="/my-projects" replace />}
        />
        
        {/* Agency New Project - With Budget */}
        <Route
          path="/agency/new-project"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager', 'customer_service']}>
              <AgencyNewProjectPage />
            </ProtectedRoute>
          }
        />
        
        {/* Redirect old project requests to projects */}
        <Route
          path="/project-requests"
          element={<Navigate to="/projects" replace />}
        />

        {/* Wallet - Client only */}
        <Route
          path="/wallet"
          element={
            <ProtectedRoute allowedRoles={['client']}>
              <WalletPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/wallet"
          element={
            <ProtectedRoute allowedRoles={['client']}>
              <WalletPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/wallet-management"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager']}>
              <WalletManagementPage />
            </ProtectedRoute>
          }
        />

        {/* Clients Wallets Management */}
        <Route
          path="/clients-wallets"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager']}>
              <ClientsWalletsPage />
            </ProtectedRoute>
          }
        />

        {/* Nomenclature Settings */}
        <Route
          path="/settings/nomenclature"
          element={
            <ProtectedRoute allowedRoles={['agency']}>
              <NomenclatureSettingsPage />
            </ProtectedRoute>
          }
        />

        {/* Client Receipts */}
        <Route
          path="/my-receipts"
          element={
            <ProtectedRoute allowedRoles={['client']}>
              <ClientReceiptsPage />
            </ProtectedRoute>
          }
        />

        {/* Client Affiliate Program */}
        <Route
          path="/affiliate-program"
          element={
            <ProtectedRoute allowedRoles={['client']}>
              <ClientAffiliatePage />
            </ProtectedRoute>
          }
        />

        {/* Profile */}
        <Route
          path="/profile"
          element={
            <ProtectedRoute allowedRoles={['agency', 'team_member', 'project_manager', 'client']}>
              <ProfilePage />
            </ProtectedRoute>
          }
        />

        {/* My Schedule - Members */}
        <Route
          path="/my-schedule"
          element={
            <ProtectedRoute allowedRoles={['team_member', 'project_manager', 'agency', 'customer_service']}>
              <MySchedulePage />
            </ProtectedRoute>
          }
        />

        {/* Settings - Agency only */}
        <Route
          path="/settings"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager']}>
              <ThemeSettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/theme"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager']}>
              <ThemeSettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/theme"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager']}>
              <ThemeSettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/calendar"
          element={
            <ProtectedRoute allowedRoles={['agency']}>
              <CalendarSettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/calendars"
          element={
            <ProtectedRoute allowedRoles={['agency']}>
              <CalendarSettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/statuses"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager']}>
              <StatusSettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/promo-banner"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager']}>
              <PromoBannerSettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/integrations/axonaut"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager']}>
              <AxonautIntegrationPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/agency-info"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager']}>
              <AgencyInfoSettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/form-builder"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager']}>
              <FormBuilderPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/templates"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager']}>
              <TemplatesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/users"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager', 'customer_service']}>
              <UsersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/team/subcontractors"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager', 'customer_service']}>
              <TeamMembersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/users/create"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager', 'customer_service']}>
              <CreateUserPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/users/:memberId/edit"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager', 'customer_service']}>
              <MemberEditPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/monthly-reports"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager', 'customer_service', 'team_member']}>
              <MonthlyReportsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/report-history"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager', 'customer_service']}>
              <ReportHistoryPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/smtp"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager']}>
              <SmtpSettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/pricing"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager']}>
              <PricingSettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/stripe"
          element={
            <ProtectedRoute allowedRoles={['agency']}>
              <StripeSettingsPage />
            </ProtectedRoute>
          }
        />

        {/* Clients */}
        <Route
          path="/clients"
          element={
            <ProtectedRoute allowedRoles={['agency', 'customer_service', 'project_manager']}>
              <ClientsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/clients/:clientId"
          element={
            <ProtectedRoute allowedRoles={['agency', 'customer_service', 'project_manager']}>
              <ClientDetailPage />
            </ProtectedRoute>
          }
        />

        {/* Creatives */}
        <Route
          path="/creatives"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager', 'customer_service']}>
              <CreativesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/project-offers"
          element={
            <ProtectedRoute allowedRoles={['agency', 'team_member']}>
              <ProjectOffersPage />
            </ProtectedRoute>
          }
        />

        {/* Payment Requests */}
        <Route
          path="/payment-requests"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager', 'customer_service']}>
              <PaymentRequestsPage />
            </ProtectedRoute>
          }
        />

        {/* Project Ratings */}
        <Route
          path="/ratings"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager', 'customer_service']}>
              <RatingsPage />
            </ProtectedRoute>
          }
        />

        {/* Satisfaction Surveys - for agency */}
        <Route
          path="/satisfaction-surveys"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager', 'customer_service']}>
              <SatisfactionSurveysPage />
            </ProtectedRoute>
          }
        />

        {/* Bonus Dashboard - for agency to manage member bonuses */}
        <Route
          path="/bonus-dashboard"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager', 'customer_service']}>
              <BonusDashboardPage />
            </ProtectedRoute>
          }
        />

        {/* Member Contracts - for agency */}
        <Route
          path="/member-contracts"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager', 'customer_service']}>
              <MemberContractsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/member-contracts/new"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager']}>
              <MemberContractCreatePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/member-contracts/:contractId"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager', 'customer_service']}>
              <MemberContractsPage />
            </ProtectedRoute>
          }
        />
        
        {/* Member Contracts - for team members (my contracts) - skip contract check to allow signing */}
        <Route
          path="/my-member-contracts"
          element={
            <ProtectedRoute allowedRoles={['team_member', 'project_manager']} skipContractCheck={true}>
              <MemberContractsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/my-member-contracts/:contractId"
          element={
            <ProtectedRoute allowedRoles={['team_member', 'project_manager']} skipContractCheck={true}>
              <MemberContractsPage />
            </ProtectedRoute>
          }
        />

        {/* Client News Management */}
        <Route
          path="/client-news"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager']}>
              <ClientNewsPage />
            </ProtectedRoute>
          }
        />

        {/* Invoices redirect */}
        <Route
          path="/invoices"
          element={<Navigate to="/dashboard" replace />}
        />
        
        {/* Expenses */}
        <Route
          path="/expenses"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager']}>
              <ExpensesPage />
            </ProtectedRoute>
          }
        />

        {/* Tickets */}
        <Route
          path="/tickets"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager', 'customer_service', 'client']}>
              <TicketsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tickets/:ticketId"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager', 'customer_service', 'client']}>
              <TicketDetailPage />
            </ProtectedRoute>
          }
        />

        {/* Campaigns */}
        <Route
          path="/campaigns"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager']}>
              <CampaignsPage />
            </ProtectedRoute>
          }
        />

        {/* Affiliates */}
        <Route
          path="/affiliates"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager', 'customer_service']}>
              <AffiliatesPageV1 />
            </ProtectedRoute>
          }
        />
        
        {/* Affiliate Contracts */}
        <Route
          path="/affiliate-contracts"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager', 'client']}>
              <AffiliateContractsPage />
            </ProtectedRoute>
          }
        />

        {/* Supports - Client */}
        <Route
          path="/my-supports"
          element={
            <ProtectedRoute allowedRoles={['client']}>
              <SupportsPage />
            </ProtectedRoute>
          }
        />

        {/* Company Profile - Client */}
        <Route
          path="/company-profile"
          element={
            <ProtectedRoute allowedRoles={['client']}>
              <CompanyProfilePage />
            </ProtectedRoute>
          }
        />

        {/* Link Tracker */}
        <Route
          path="/tools/link-tracker"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager']}>
              <LinkTrackerPage />
            </ProtectedRoute>
          }
        />

        {/* QR Code Generator */}
        <Route
          path="/tools/qrcode"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager']}>
              <QRCodePage />
            </ProtectedRoute>
          }
        />

        {/* Contracts */}
        <Route
          path="/contracts"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager', 'customer_service']}>
              <ContractsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/client-contracts"
          element={
            <ProtectedRoute allowedRoles={['client']}>
              <ContractsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/client-forfaits"
          element={
            <ProtectedRoute allowedRoles={['client']}>
              <ClientForfaitsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/subscribe"
          element={
            <ProtectedRoute allowedRoles={['client']}>
              <ContractSubscriptionPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/contracts/:id"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager', 'customer_service', 'client']}>
              <ContractDetailPage />
            </ProtectedRoute>
          }
        />

        {/* Prospects */}
        <Route
          path="/prospects"
          element={
            <ProtectedRoute allowedRoles={['agency', 'customer_service', 'project_manager']}>
              <ProspectsPageV1 />
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/prospects/:prospectId"
          element={
            <ProtectedRoute allowedRoles={['agency', 'customer_service', 'project_manager']}>
              <ProspectDetailPage />
            </ProtectedRoute>
          }
        />

        {/* Receipts */}
        <Route
          path="/receipts/pending"
          element={
            <ProtectedRoute allowedRoles={['agency', 'customer_service']}>
              <ReceiptsPageV1 />
            </ProtectedRoute>
          }
        />
        <Route
          path="/receipts/manage"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager']}>
              <AgencyReceiptsPage />
            </ProtectedRoute>
          }
        />

        {/* Reports */}
        <Route
          path="/reports"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager', 'customer_service']}>
              <ReportsPage />
            </ProtectedRoute>
          }
        />

        {/* Team Tasks */}
        <Route
          path="/my-tasks"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager', 'customer_service', 'team_member']}>
              <TeamTasksPage />
            </ProtectedRoute>
          }
        />
        
        {/* Task Detail Page */}
        <Route
          path="/tasks/:taskId"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager', 'customer_service', 'team_member', 'client']}>
              <TaskDetailPage />
            </ProtectedRoute>
          }
        />

        {/* Unified Chat Page - Main chat hub */}
        <Route
          path="/chat"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager', 'customer_service', 'team_member', 'creative', 'client']}>
              <UnifiedChatPage />
            </ProtectedRoute>
          }
        />

        {/* Task-based Chat - conversation per task */}
        <Route
          path="/project/:projectId/chat"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager', 'customer_service', 'team_member', 'creative', 'client']}>
              <TaskChatPage />
            </ProtectedRoute>
          }
        />

        {/* Contract-based Chat - conversation per maintenance task */}
        <Route
          path="/contract/:contractId/chat"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager', 'customer_service', 'team_member', 'creative', 'client']}>
              <ContractChatPage />
            </ProtectedRoute>
          }
        />

        {/* Legacy Internal Chat - redirect to unified */}
        <Route
          path="/internal-chat"
          element={<Navigate to="/chat" replace />}
        />

        {/* Centralized Project Chat - redirect to unified */}
        <Route
          path="/project-chat"
          element={<Navigate to="/chat" replace />}
        />

        {/* Saved Links */}
        <Route
          path="/saved-links"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager', 'customer_service', 'team_member', 'creative']}>
              <SavedLinksPage />
            </ProtectedRoute>
          }
        />

        {/* Technical Documentation */}
        <Route
          path="/documentation"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager', 'customer_service', 'team_member', 'client']}>
              <TechnicalDocsPage />
            </ProtectedRoute>
          }
        />

        {/* Team Invoices */}
        <Route
          path="/team-invoices"
          element={
            <ProtectedRoute allowedRoles={['team_member']}>
              <InvoicesPage />
            </ProtectedRoute>
          }
        />

        {/* Member Bonuses */}
        <Route
          path="/my-bonuses"
          element={
            <ProtectedRoute allowedRoles={['team_member']}>
              <MemberBonusesPage />
            </ProtectedRoute>
          }
        />

        {/* Member Reports */}
        <Route
          path="/my-reports"
          element={
            <ProtectedRoute allowedRoles={['team_member']}>
              <MemberReportsPage />
            </ProtectedRoute>
          }
        />

        {/* Offers - Client */}
        <Route
          path="/offers"
          element={
            <ProtectedRoute allowedRoles={['client']}>
              <OffersPage />
            </ProtectedRoute>
          }
        />

        {/* Wallet History */}
        <Route
          path="/wallet/history"
          element={
            <ProtectedRoute allowedRoles={['client']}>
              <WalletHistoryPage />
            </ProtectedRoute>
          }
        />

        {/* Quotes Management */}
        <Route
          path="/quotes"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager']}>
              <QuotesManagementPage />
            </ProtectedRoute>
          }
        />

        {/* Knowledge Base */}
        <Route
          path="/knowledge"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager', 'customer_service', 'team_member', 'client']}>
              <KnowledgeBasePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/knowledge/:articleId"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager', 'customer_service', 'team_member', 'client']}>
              <KnowledgeBasePage />
            </ProtectedRoute>
          }
        />

        {/* Team Planning */}
        <Route
          path="/planning"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager', 'customer_service', 'team_member']}>
              <TeamPlanningPage />
            </ProtectedRoute>
          }
        />

        {/* Notifications */}
        <Route
          path="/notifications"
          element={
            <ProtectedRoute allowedRoles={['agency', 'team_member', 'client']}>
              <NotificationsPage />
            </ProtectedRoute>
          }
        />

        {/* Credits */}
        <Route
          path="/credits"
          element={
            <ProtectedRoute allowedRoles={['agency', 'team_member', 'client']}>
              <CreditsPage />
            </ProtectedRoute>
          }
        />

        {/* Social Dashboard */}
        <Route
          path="/social"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager']}>
              <SocialDashboardPage />
            </ProtectedRoute>
          }
        />

        {/* AI Assistant */}
        <Route
          path="/assistant"
          element={
            <ProtectedRoute allowedRoles={['agency', 'team_member', 'client']}>
              <AssistantPage />
            </ProtectedRoute>
          }
        />

        {/* AI Summaries History */}
        <Route
          path="/summaries"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager']}>
              <SummaryHistoryPage />
            </ProtectedRoute>
          }
        />

        {/* Onboarding Routes */}
        <Route
          path="/onboarding/client"
          element={
            <ProtectedRoute allowedRoles={['client']}>
              <ClientOnboardingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/onboarding/team"
          element={
            <ProtectedRoute allowedRoles={['team_member']}>
              <TeamOnboardingPage />
            </ProtectedRoute>
          }
        />
        <Route path="/devenir-partenaire" element={<CreativeOnboardingPage />} />
        
        {/* Internal Creative Directory */}
        <Route
          path="/annuaire-creatifs"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager']}>
              <CreativeDirectoryPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/creatif/:creativeId"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager']}>
              <CreativeProfilePage />
            </ProtectedRoute>
          }
        />
        
        {/* Onboarding Management */}
        <Route
          path="/onboarding-management"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager']}>
              <OnboardingManagementPage />
            </ProtectedRoute>
          }
        />

        {/* Guided Tour Settings */}
        <Route
          path="/settings/guided-tour"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager']}>
              <GuidedTourSettingsPage />
            </ProtectedRoute>
          }
        />

        {/* Shared Passwords */}
        <Route
          path="/shared-passwords"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager', 'customer_service', 'client']}>
              <SharedPasswordsPage />
            </ProtectedRoute>
          }
        />

        {/* Shared Supports */}
        <Route
          path="/shared-supports"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager', 'customer_service', 'client']}>
              <SharedSupportsPage />
            </ProtectedRoute>
          }
        />

        {/* Google Drive Management - Agency */}
        <Route
          path="/google-drive"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager', 'customer_service']}>
              <GoogleDriveManagementPage />
            </ProtectedRoute>
          }
        />

        {/* Admin Page */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager']}>
              <AdminPage />
            </ProtectedRoute>
          }
        />

        {/* Admin Tools - Bugs, Docs, Tutorials, Versions */}
        <Route
          path="/admin/tools"
          element={
            <ProtectedRoute allowedRoles={['agency']}>
              <AdminToolsPage />
            </ProtectedRoute>
          }
        />

        {/* Roadmap & Bugs - Agency only */}
        <Route
          path="/admin/roadmap"
          element={
            <ProtectedRoute allowedRoles={['agency']}>
              <RoadmapPage />
            </ProtectedRoute>
          }
        />

        {/* Feature Tracking - Agency only */}
        <Route
          path="/admin/features"
          element={
            <ProtectedRoute allowedRoles={['agency']}>
              <FeatureTrackingPage />
            </ProtectedRoute>
          }
        />

        {/* Announcements Management - Agency and PM */}
        <Route
          path="/admin/announcements"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager']}>
              <AnnouncementsAdminPage />
            </ProtectedRoute>
          }
        />

        {/* Gift Codes Management - Agency and PM */}
        <Route
          path="/admin/gift-codes"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager']}>
              <GiftCodesPage />
            </ProtectedRoute>
          }
        />

        {/* Recovery Management - Agency and PM */}
        <Route
          path="/admin/recovery"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager']}>
              <RecoveryManagementPage />
            </ProtectedRoute>
          }
        />

        {/* Client Invoices Page - All roles */}
        <Route
          path="/my-invoices"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager', 'client']}>
              <ClientInvoicesPage />
            </ProtectedRoute>
          }
        />

        {/* Client Schedules Page */}
        <Route
          path="/my-schedules"
          element={
            <ProtectedRoute allowedRoles={['client']}>
              <ClientSchedulesPage />
            </ProtectedRoute>
          }
        />

        {/* Client Contracts Page */}
        <Route
          path="/my-contracts"
          element={
            <ProtectedRoute allowedRoles={['client']}>
              <ClientContractsPage />
            </ProtectedRoute>
          }
        />

        {/* Help Page - accessible to clients, members, agency */}
        <Route
          path="/help"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager', 'team_member', 'client', 'customer_service']}>
              <HelpPage />
            </ProtectedRoute>
          }
        />

        {/* Admin Settings */}
        <Route
          path="/admin/settings"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager']}>
              <AdminSettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/agency/manifest"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager']}>
              <AgencyManifestPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/agency/brief-templates"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager']}>
              <BriefTemplatesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/intern/dashboard"
          element={
            <ProtectedRoute allowedRoles={['intern']}>
              <InternDashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/demo-mode"
          element={
            <ProtectedRoute allowedRoles={['agency']}>
              <DemoModePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/embed-forms"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager']}>
              <EmbedFormsPage />
            </ProtectedRoute>
          }
        />

        {/* Changelog / Version History */}
        <Route
          path="/changelog"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager', 'customer_service']}>
              <ChangelogPage />
            </ProtectedRoute>
          }
        />

        {/* Domains Management */}
        <Route
          path="/domains"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager', 'customer_service', 'client']}>
              <DomainsPage />
            </ProtectedRoute>
          }
        />

        {/* Service Invoices - Agency */}
        <Route
          path="/service-invoices"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager', 'customer_service']}>
              <ServiceInvoicesPage />
            </ProtectedRoute>
          }
        />

        {/* Service Invoices - Client */}
        <Route
          path="/my-services"
          element={
            <ProtectedRoute allowedRoles={['client']}>
              <ServiceInvoicesPage />
            </ProtectedRoute>
          }
        />

        {/* Email Settings */}
        <Route
          path="/email-settings"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager']}>
              <EmailSettingsPage />
            </ProtectedRoute>
          }
        />

        {/* Hourly Rates */}
        <Route
          path="/hourly-rates"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager']}>
              <HourlyRatesPage />
            </ProtectedRoute>
          }
        />

        {/* Third Party Services */}
        <Route
          path="/third-party-services"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager']}>
              <ThirdPartyServicesPage />
            </ProtectedRoute>
          }
        />

        {/* Payment Schedules */}
        <Route
          path="/payment-schedules"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager', 'customer_service']}>
              <PaymentSchedulesPage />
            </ProtectedRoute>
          }
        />

        {/* Contract Templates - Settings page for managing templates */}
        <Route
          path="/contract-templates"
          element={
            <ProtectedRoute allowedRoles={['agency']}>
              <ContractTemplatesPage />
            </ProtectedRoute>
          }
        />

        {/* Smart Contracts - AI Generation */}
        <Route
          path="/smart-contracts"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager']}>
              <SmartContractsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/contract-generator"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager']}>
              <ContractGeneratorPage />
            </ProtectedRoute>
          }
        />

        {/* Develop Page */}
        <Route
          path="/develop"
          element={
            <ProtectedRoute allowedRoles={['agency', 'project_manager']}>
              <DevelopPage />
            </ProtectedRoute>
          }
        />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

// Onboarding wrapper component
function OnboardingWrapper({ children }) {
  const { needsOnboarding, refreshUser, user } = useAuth();
  const [showOnboarding, setShowOnboarding] = useState(true);
  
  // Don't show onboarding for agency users
  if (!needsOnboarding || user?.role === 'agency') {
    return children;
  }
  
  const handleComplete = async () => {
    setShowOnboarding(false);
    await refreshUser();
  };
  
  return (
    <>
      {children}
      <OnboardingModal 
        isOpen={showOnboarding && needsOnboarding} 
        onComplete={handleComplete} 
      />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ThemeProvider>
            <TimerProvider>
              <OnboardingWrapper>
                <AppRoutes />
              </OnboardingWrapper>
              <BugReportButton />
              <InstallAppButton className="hidden" showBadge={false} />
              <Toaster position="top-right" richColors />
            </TimerProvider>
          </ThemeProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
